import { recoverToLatestBuild } from '@/lib/helpers/appUpdateRecovery.ts'
import { decideChunkRecoveryAction } from '@/lib/helpers/chunkRecoveryDecisions.ts'
import { probeConnectivity } from '@/lib/connectivityProbe.ts'

/**
 * Global handler for chunk loading errors (dynamic import failures).
 *
 * When a new version is deployed, old JS chunks are removed. If a user
 * has a stale index.html that references old chunk hashes, dynamic imports
 * will fail with a 404. This handler detects such failures and triggers
 * a page reload to fetch the new version.
 */

const RELOAD_STORAGE_KEY = 'chunk_error_reload_timestamp'
const RELOAD_COOLDOWN_MS = 10000 // 10 seconds cooldown to prevent reload loops

function shouldReload(): boolean {
  const lastReload = localStorage.getItem(RELOAD_STORAGE_KEY)
  if (!lastReload) return true

  const timeSinceLastReload = Date.now() - parseInt(lastReload, 10)
  return timeSinceLastReload > RELOAD_COOLDOWN_MS
}

function markReloadAttempt(): void {
  localStorage.setItem(RELOAD_STORAGE_KEY, Date.now().toString())
}

function clearReloadMarker(): void {
  localStorage.removeItem(RELOAD_STORAGE_KEY)
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  const message = error.message.toLowerCase()

  // Common chunk load error patterns
  const patterns = [
    'failed to fetch dynamically imported module',
    'loading chunk',
    'loading css chunk',
    'dynamically imported module',
    'importing a module script failed',
    'failed to load module script',
    'error loading dynamically imported module',
  ]

  return patterns.some(pattern => message.includes(pattern))
}

async function handleChunkError(): Promise<void> {
  // Gate recovery behind connectivity checks. Clearing Cache Storage while
  // the app is offline would wipe the precached app shell (workbox-precache,
  // app-shell, offline.html) and guarantee a white screen on the forced
  // reload — the exact failure mode recovery is meant to prevent online.
  const inCooldown = !shouldReload()
  const isOnline = navigator.onLine
  const probeOnline = isOnline && !inCooldown ? await probeConnectivity() : false

  const decision = decideChunkRecoveryAction({ isOnline, inCooldown, probeOnline })

  if (decision.action === 'skip') {
    switch (decision.reason) {
      case 'cooldown':
        console.warn('[ChunkErrorHandler] Reload cooldown active, skipping reload')
        return
      case 'offline':
        console.warn(
          '[ChunkErrorHandler] Offline — preserving caches so the offline shell stays usable',
        )
        return
      case 'unreachable':
        console.warn(
          '[ChunkErrorHandler] Server unreachable despite navigator.onLine — preserving caches',
        )
        return
    }
  }

  markReloadAttempt()

  await recoverToLatestBuild({
    reloadParam: '_reload',
    logPrefix: 'ChunkErrorHandler',
  })
}

export function setupChunkErrorHandler(): void {
  // Clear reload marker on successful load (after a delay to ensure app is stable)
  setTimeout(() => {
    clearReloadMarker()
  }, 5000)

  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      console.error('[ChunkErrorHandler] Chunk load error detected:', event.reason)
      event.preventDefault()
      handleChunkError()
    }
  })

  // Handle regular errors (fallback)
  window.addEventListener('error', (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      console.error('[ChunkErrorHandler] Chunk load error detected:', event.error)
      event.preventDefault()
      handleChunkError()
    }
  })

  // Listen for SW notification about chunk 404
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === 'CHUNK_LOAD_ERROR') {
        console.error('[ChunkErrorHandler] SW reported chunk 404:', event.data.url)
        handleChunkError()
      }
    })
  }

  console.log('[ChunkErrorHandler] Initialized')
}
